/**
 * Cliente HTTP da API.
 *
 * Todas as chamadas usam caminho relativo `/api/...`. Quem repassa para o
 * backend e o proxy: o Vite em desenvolvimento, o nginx em producao. Assim a
 * mesma build funciona em qualquer ambiente, sem URL embutida.
 *
 * `credentials: 'include'` e obrigatorio: sem ele o navegador nao envia o
 * cookie de sessao, e toda requisicao autenticada volta 401.
 */

const API_PREFIX = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues?: FieldIssue[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface FieldIssue {
  field: string;
  message: string;
}

interface ErrorBody {
  message?: string;
  issues?: FieldIssue[];
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    const errorBody = (body ?? {}) as ErrorBody;

    throw new ApiError(
      errorBody.message ?? `Falha na requisicao (HTTP ${response.status})`,
      response.status,
      errorBody.issues,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
};

// --- Tipos do dominio -------------------------------------------------------

export type UserRole = 'PROFESSOR' | 'STUDENT';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  createdAt: string;
}

export interface HealthReadyResponse {
  status: string;
  service: string;
  database: string;
  timestamp: string;
}

// --- Chamadas ---------------------------------------------------------------

export const getBackendHealth = () => api.get<HealthReadyResponse>('/health/ready');

export const authApi = {
  me: () => api.get<{ user: User }>('/auth/me'),
  /** Com verificação em dois passos ativa (PRD 39.2), a senha certa NÃO abre
   * a sessão: volta `twoFactorRequired` e um desafio, e a sessão só abre em
   * `loginTwoFactor`. */
  login: (email: string, password: string) =>
    api.post<{ user: User } | { twoFactorRequired: true; challenge: string }>('/auth/login', {
      email,
      password,
    }),
  /** Segunda etapa do login: o desafio recebido após a senha + o código do
   * aplicativo (6 dígitos) ou um código de recuperação. */
  loginTwoFactor: (challenge: string, code: string) =>
    api.post<{ user: User }>('/auth/login/2fa', { challenge, code }),
  /** Não existe cadastro público (PRD 33.1) — sempre exige o token do convite
   * da turma, e a matrícula já acontece junto. */
  register: (name: string, email: string, password: string, inviteToken: string) =>
    api.post<{ user: User; classId: string }>('/auth/register', {
      name,
      email,
      password,
      inviteToken,
    }),
  logout: () => api.post<void>('/auth/logout'),
  /** "Sair de todos os aparelhos" (PRD 38.1): encerra as sessões da conta em
   * todos os navegadores, inclusive este. O `logout` acima sai só deste. */
  logoutAll: () => api.post<void>('/auth/logout-all'),
  /** Troca da própria senha (PRD 34.2) — professor ou aluno, sempre sobre a
   * sessão atual. */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/auth/password', { currentPassword, newPassword }),
};

/** Verificação em dois passos do professor (PRD 39.2). */
export interface TwoFactorStatus {
  enabled: boolean;
  recoveryCodesLeft: number;
}

export interface TwoFactorSetup {
  /** Chave em texto, para digitar à mão se o QR Code não puder ser lido. */
  secret: string;
  qrCodeSvg: string;
}

export const twoFactorApi = {
  status: () => api.get<TwoFactorStatus>('/auth/2fa'),
  setup: () => api.post<TwoFactorSetup>('/auth/2fa/setup'),
  /** Confirma com um código e devolve os códigos de recuperação — a única vez
   * em que aparecem. */
  enable: (code: string) => api.post<{ recoveryCodes: string[] }>('/auth/2fa/enable', { code }),
  disable: (password: string, code: string) =>
    api.post<void>('/auth/2fa/disable', { password, code }),
  regenerateRecoveryCodes: (password: string, code: string) =>
    api.post<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes', { password, code }),
};

/** Ativação de conta pré-cadastrada (PRD 33.7) — público, o token na URL é a
 * própria autorização. Também serve à redefinição de senha (PRD 34.3), sobre
 * uma conta já ativada. */
export const activateApi = {
  peek: (token: string) => api.get<{ name: string; email: string }>(`/activate/${token}`),
  activate: (token: string, password: string) =>
    api.post<{ user: User }>(`/activate/${token}`, { password }),
};

export const usersApi = {
  list: () => api.get<{ users: User[]; total: number }>('/users'),
  /** Exclusão definitiva da conta de um aluno (PRD 34.4). */
  deleteStudent: (userId: string) => api.delete<void>(`/users/${userId}`),
};

// --- Turmas e grupos --------------------------------------------------------

export type AcademicLevel = 'UNDERGRADUATE' | 'GRADUATE';
export type ClassStatus = 'ACTIVE' | 'ARCHIVED';

export interface SchoolClass {
  id: string;
  name: string;
  discipline: string;
  course: string;
  level: AcademicLevel;
  entrySemester: string;
  disciplineSemester: number;
  academicTerm: string;
  status: ClassStatus;
  /** Habilita Meus Fluxos para quem está matriculado (PRD 34.5). */
  myFlowsEnabled: boolean;
  createdAt: string;
  /** Somente na visao do professor. */
  _count?: { members: number; groups: number };
  /** Somente na visao do aluno. */
  professorName?: string;
  registrationNumber?: string | null;
}

export interface ClassMember {
  /** Id da matrícula (`ClassMember`), usado nas rotas de turma. */
  id: string;
  /** Id do usuário — necessário para excluir a conta (PRD 34.4), que é
   * global, não por turma. */
  userId: string;
  name: string;
  email: string;
  registrationNumber: string | null;
  joinedAt: string;
  /** Falso enquanto o aluno cadastrado pelo professor nao definiu senha. */
  activated: boolean;
  groups: { id: string; name: string }[];
}

export interface Group {
  id: string;
  name: string;
  classId: string;
  leaderId: string | null;
  createdAt: string;
  members: {
    classMemberId: string;
    name: string;
    email: string;
    registrationNumber: string | null;
    isLeader: boolean;
  }[];
}

export interface Invite {
  url: string;
  qrCodeSvg: string;
  /** Validade de 30 min (PRD 33.3) — nulo só em turmas/grupos anteriores à
   * migração, tratado como já expirado. */
  expiresAt: string | null;
}

export interface CreateClassPayload {
  name: string;
  discipline: string;
  course: string;
  level: AcademicLevel;
  entrySemester: string;
  disciplineSemester: number;
  academicTerm: string;
}

/** Prévia da exclusão de turma (PRD 34.7) — números reais antes do passo
 * irreversível. */
export interface ClassDeletionPreview {
  className: string;
  status: ClassStatus;
  memberCount: number;
  activityCount: number;
  instanceCount: number;
  /** Contas que seriam apagadas se a caixa "apagar contas" for marcada. */
  deletableAccountCount: number;
  /** Poupadas por estarem matriculadas em outra turma também. */
  sparedAccountCount: number;
}

export const classesApi = {
  list: () => api.get<{ classes: SchoolClass[]; viewer: 'PROFESSOR' | 'STUDENT' }>('/classes'),
  get: (classId: string) =>
    api.get<{ class: SchoolClass; viewer: 'PROFESSOR' | 'STUDENT' }>(`/classes/${classId}`),
  create: (payload: CreateClassPayload) =>
    api.post<{ class: SchoolClass }>('/classes', payload),
  update: (
    classId: string,
    payload: Partial<CreateClassPayload> & { status?: ClassStatus; myFlowsEnabled?: boolean },
  ) => api.patch<{ class: SchoolClass }>(`/classes/${classId}`, payload),

  /** Prévia da exclusão (PRD 34.7). */
  deletePreview: (classId: string) =>
    api.get<ClassDeletionPreview>(`/classes/${classId}/delete-preview`),
  /** Exclusão definitiva — só permitida com a turma já arquivada. */
  remove: (classId: string, payload: { confirmName: string; deleteAccounts: boolean }) =>
    api.delete<void>(`/classes/${classId}`, payload),

  invite: (classId: string) => api.get<Invite>(`/classes/${classId}/invite`),
  rotateInvite: (classId: string) => api.post<Invite>(`/classes/${classId}/invite/rotate`),
  cancelInvite: (classId: string) => api.post<void>(`/classes/${classId}/invite/cancel`),

  members: (classId: string) =>
    api.get<{ members: ClassMember[]; total: number }>(`/classes/${classId}/members`),
  addMember: (classId: string, payload: { name: string; email: string; registrationNumber?: string }) =>
    api.post<{ members: ClassMember[] }>(`/classes/${classId}/members`, payload),
  removeMember: (classId: string, memberId: string) =>
    api.delete<void>(`/classes/${classId}/members/${memberId}`),

  /** Link de ativação individual (PRD 33.7) — `url: null` quando a conta já
   * foi ativada. */
  activationLink: (classId: string, memberId: string) =>
    api.get<{ url: string | null; expiresAt: string | null }>(
      `/classes/${classId}/members/${memberId}/activation`,
    ),
  regenerateActivationLink: (classId: string, memberId: string) =>
    api.post<{ url: string; expiresAt: string }>(
      `/classes/${classId}/members/${memberId}/activation/regenerate`,
    ),
  /** Redefinição de senha de aluno já ativado (PRD 34.3) — mesmo formato de
   * link da ativação. */
  resetMemberPassword: (classId: string, memberId: string) =>
    api.post<{ url: string; expiresAt: string }>(
      `/classes/${classId}/members/${memberId}/reset-password`,
    ),

  peekInvite: (token: string) =>
    api.get<{
      class: {
        id: string;
        name: string;
        discipline: string;
        course: string;
        academicTerm: string;
        professorName: string;
      };
    }>(`/invites/turma/${token}`),
  acceptInvite: (token: string) =>
    api.post<{ classId: string; alreadyMember: boolean }>(`/invites/turma/${token}/accept`),
};

export const groupsApi = {
  list: (classId: string) =>
    api.get<{ groups: Group[]; total: number }>(`/classes/${classId}/groups`),
  create: (classId: string, name: string) =>
    api.post<{ group: Group }>(`/classes/${classId}/groups`, { name }),
  update: (groupId: string, payload: { name?: string; leaderId?: string | null }) =>
    api.patch<{ group: Group }>(`/groups/${groupId}`, payload),
  remove: (groupId: string) => api.delete<void>(`/groups/${groupId}`),

  invite: (groupId: string) => api.get<Invite>(`/groups/${groupId}/invite`),
  rotateInvite: (groupId: string) => api.post<Invite>(`/groups/${groupId}/invite/rotate`),
  cancelInvite: (groupId: string) => api.post<void>(`/groups/${groupId}/invite/cancel`),

  addMember: (groupId: string, classMemberId: string) =>
    api.post<{ group: Group }>(`/groups/${groupId}/members`, { classMemberId }),
  removeMember: (groupId: string, classMemberId: string) =>
    api.delete<{ group: Group }>(`/groups/${groupId}/members/${classMemberId}`),

  peekInvite: (token: string) =>
    api.get<{
      group: { id: string; name: string; classId: string; className: string; discipline: string };
    }>(`/invites/grupo/${token}`),
  acceptInvite: (token: string) =>
    api.post<{ groupId: string; classId: string; alreadyMember: boolean }>(
      `/invites/grupo/${token}/accept`,
    ),
};

// --- Atividades e módulos ---------------------------------------------------

export type ActivityType = 'INDIVIDUAL' | 'GROUP';
export type ActivityFlowType = 'AS_IS' | 'TO_BE';
export type ActivityStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type ModuleType =
  | 'FUTURE_VISION'
  | 'SIPOC'
  | 'BPMN'
  | 'PROCESS_ANALYSIS'
  | 'PROBLEM'
  | 'ISHIKAWA'
  | 'OKR'
  | 'TO_BE'
  | 'IMPLEMENTATION_PLAN'
  | 'FIVE_W'
  | 'GANTT'
  | 'PDCA';

export interface Activity {
  id: string;
  classId: string;
  title: string;
  description: string | null;
  type: ActivityType;
  flowType: ActivityFlowType | null;
  status: ActivityStatus;
  createdAt: string;
  releasedModules: ModuleType[];
  /** Somente na visão do professor. */
  instanceCount?: number;
}

export interface ModuleState {
  moduleType: ModuleType;
  enabled: boolean;
  releasedAt: string | null;
  releasedByName: string | null;
}

export interface ActivityInstanceSummary {
  id: string;
  ownerType: 'GROUP' | 'STUDENT';
  ownerName: string;
  memberCount: number;
  createdAt: string;
}

export interface MyInstance {
  instanceId: string;
  ownerType: 'GROUP' | 'STUDENT';
  groupName?: string;
}

export const activitiesApi = {
  list: (classId: string) =>
    api.get<{ activities: Activity[]; total: number }>(`/classes/${classId}/activities`),
  create: (
    classId: string,
    payload: {
      title: string;
      description?: string;
      type: ActivityType;
      flowType?: ActivityFlowType;
    },
  ) => api.post<{ activity: Activity }>(`/classes/${classId}/activities`, payload),
  update: (
    activityId: string,
    payload: {
      title?: string;
      description?: string | null;
      status?: ActivityStatus;
      flowType?: ActivityFlowType | null;
    },
  ) => api.patch<{ activity: Activity }>(`/activities/${activityId}`, payload),
  remove: (activityId: string) => api.delete<void>(`/activities/${activityId}`),

  modules: (activityId: string) =>
    api.get<{ modules: ModuleState[] }>(`/activities/${activityId}/modules`),
  setModule: (activityId: string, moduleType: ModuleType, enabled: boolean) =>
    api.put<{ modules: ModuleState[] }>(
      `/activities/${activityId}/modules/${moduleType}`,
      { enabled },
    ),

  instances: (activityId: string) =>
    api.get<{ instances: ActivityInstanceSummary[]; total: number }>(
      `/activities/${activityId}/instances`,
    ),
  syncInstances: (activityId: string) =>
    api.post<{ created: number; total: number }>(`/activities/${activityId}/instances/sync`),

  myInstance: (activityId: string) =>
    api.get<MyInstance>(`/activities/${activityId}/my-instance`),
};

/** Nomes dos módulos em português, na ordem da sequência pedagógica sugerida. */
export const MODULE_LABELS: Record<ModuleType, string> = {
  FUTURE_VISION: 'Visão Futura',
  SIPOC: 'SIPOC',
  BPMN: 'BPMN AS-IS',
  PROCESS_ANALYSIS: 'Análise do Processo',
  PROBLEM: 'Problemas',
  ISHIKAWA: 'Ishikawa (4M)',
  OKR: 'Objetivos e Metas (OKR)',
  TO_BE: 'BPMN TO-BE',
  IMPLEMENTATION_PLAN: 'Plano de Implantação',
  FIVE_W: '5W1H',
  GANTT: 'Cronograma / Gantt',
  PDCA: 'PDCA',
};

export const MODULE_ORDER: ModuleType[] = [
  'FUTURE_VISION',
  'SIPOC',
  'BPMN',
  'PROCESS_ANALYSIS',
  'PROBLEM',
  'ISHIKAWA',
  'OKR',
  'TO_BE',
  'IMPLEMENTATION_PLAN',
  'FIVE_W',
  'GANTT',
  'PDCA',
];

// --- Feedback do professor (PRD 36) ------------------------------------------

/** Ishikawa fica fora (36.6, D-166) — não existe `FeedbackModuleType` próprio
 * no frontend: usar `ModuleType` normalmente, só nunca passando `ISHIKAWA`
 * pra `feedbackApi`. */
export interface ActivityFeedback {
  id: string;
  moduleType: ModuleType;
  text: string;
  /** Preenchido só em BPMN/TO-BE quando havia algo a congelar (36.3/36.4). */
  bpmnVersionId: string | null;
  createdAt: string;
  updatedAt: string;
  authorName: string | null;
}

export const feedbackApi = {
  list: (instanceId: string, moduleType: ModuleType) =>
    api.get<{ feedbacks: ActivityFeedback[] }>(`/instances/${instanceId}/feedback/${moduleType}`),
  create: (instanceId: string, moduleType: ModuleType, text: string) =>
    api.post<{ feedback: ActivityFeedback; frozeSkipped: boolean }>(
      `/instances/${instanceId}/feedback/${moduleType}`,
      { text },
    ),
  update: (feedbackId: string, text: string) =>
    api.patch<{ feedback: ActivityFeedback }>(`/feedback/${feedbackId}`, { text }),
  remove: (feedbackId: string) => api.delete<void>(`/feedback/${feedbackId}`),
};

// --- BPMN -------------------------------------------------------------------

export interface CopiedFromInfo {
  versionNumber: number;
  label: string | null;
  groupName: string | null;
  copiedAt: string | null;
}

export interface BpmnDiagram {
  id: string;
  name: string | null;
  xml: string;
  revision: number;
  updatedAt: string;
  updatedBy: { name: string } | null;
  canWrite: boolean;
  isProfessor: boolean;
  /** Qual módulo controla esta atividade — BPMN ou TO_BE. Usado pela FASE 8
   *  para abrir a sessão de participação certa. Ausente em Meus Fluxos. */
  moduleType?: ModuleType;
  /** Preenchido só em projetos TO-BE que nasceram de uma cópia (Fase 13). */
  copiedFrom?: CopiedFromInfo | null;
  /** Dono do projeto acadêmico (nome do grupo, ou do aluno em atividade
   * individual) — usado no nome do arquivo do PNG exportado (D-163).
   * Ausente em Meus Fluxos. */
  ownerName?: string | null;
  /** Identificação completa (nome + e-mail + matrícula de cada integrante)
   * — embutida como metadado do PNG exportado, pra sobreviver a um
   * renomeio do arquivo. */
  ownerIdentity?: string | null;
}

export interface CopyCandidate {
  id: string;
  versionNumber: number;
  label: string | null;
  frozenAt: string;
  activityTitle: string | null;
}

export interface SaveResult {
  revision: number;
  updatedAt: string;
}

export interface MyFlow {
  id: string;
  name: string | null;
  revision: number;
  updatedAt: string;
}

export const bpmnApi = {
  /** Diagrama do projeto acadêmico (instância de atividade). */
  get: (instanceId: string) =>
    api.get<{ diagram: BpmnDiagram }>(`/instances/${instanceId}/bpmn`),
  save: (instanceId: string, xml: string, revision: number) =>
    api.put<SaveResult>(`/instances/${instanceId}/bpmn`, { xml, revision }),
  /** URL de download do XML. Somente professor — o backend recusa aluno. */
  exportUrl: (instanceId: string) => `/api/instances/${instanceId}/bpmn/export`,
  /** ZIP com um .bpmn por projeto da atividade (Fase 17). Somente professor. */
  exportAllUrl: (activityId: string) => `/api/activities/${activityId}/bpmn/export-all`,

  /** Só faz sentido numa atividade TO-BE (Fase 13). */
  copyCandidates: (instanceId: string) =>
    api.get<{ versions: CopyCandidate[] }>(`/instances/${instanceId}/bpmn/copy-candidates`),
  copyFrom: (instanceId: string, sourceVersionId: string) =>
    api.post<{ diagram: BpmnDiagram }>(`/instances/${instanceId}/bpmn/copy`, { sourceVersionId }),
};

export const myFlowsApi = {
  list: () => api.get<{ flows: MyFlow[] }>('/my-flows'),
  create: (name: string) => api.post<{ flow: MyFlow }>('/my-flows', { name }),
  get: (flowId: string) => api.get<{ diagram: BpmnDiagram }>(`/my-flows/${flowId}`),
  save: (flowId: string, xml: string, revision: number) =>
    api.put<SaveResult>(`/my-flows/${flowId}`, { xml, revision }),
  rename: (flowId: string, name: string) =>
    api.patch<void>(`/my-flows/${flowId}`, { name }),
  remove: (flowId: string) => api.delete<void>(`/my-flows/${flowId}`),
};

// --- Versões congeladas (Fase 6) --------------------------------------------

export type BpmnVersionStatus = 'FROZEN' | 'DISCARDED';

export type BpmnElementKind =
  | 'START_EVENT'
  | 'END_EVENT'
  | 'INTERMEDIATE_EVENT'
  | 'ACTIVITY'
  | 'GATEWAY'
  | 'SUBPROCESS';

export type BpmnElementSubtype =
  | 'GENERIC_TASK'
  | 'USER_TASK'
  | 'SERVICE_TASK'
  | 'SCRIPT_TASK'
  | 'EXCLUSIVE_GATEWAY'
  | 'INCLUSIVE_GATEWAY'
  | 'PARALLEL_GATEWAY'
  | 'COMPLEX_GATEWAY'
  | 'OTHER';

export interface BpmnVersionSummary {
  id: string;
  versionNumber: number;
  label: string | null;
  status: BpmnVersionStatus;
  frozenAt: string;
  frozenByName: string | null;
  elementCount: number;
}

export interface BpmnVersionElement {
  elementId: string;
  kind: BpmnElementKind;
  subtype: BpmnElementSubtype;
  name: string | null;
  order: number;
}

export interface BpmnVersionDetail {
  id: string;
  versionNumber: number;
  label: string | null;
  status: BpmnVersionStatus;
  xml: string;
  frozenAt: string;
  frozenByName: string | null;
  elements: BpmnVersionElement[];
}

export const versionsApi = {
  list: (instanceId: string) =>
    api.get<{ versions: BpmnVersionSummary[] }>(`/instances/${instanceId}/bpmn/versions`),
  freeze: (instanceId: string, label?: string) =>
    api.post<{ version: { id: string; versionNumber: number; elementCount: number } }>(
      `/instances/${instanceId}/bpmn/versions`,
      label ? { label } : {},
    ),
  get: (versionId: string) =>
    api.get<{ version: BpmnVersionDetail }>(`/bpmn/versions/${versionId}`),
  setStatus: (versionId: string, status: BpmnVersionStatus) =>
    api.patch<void>(`/bpmn/versions/${versionId}`, { status }),
};

/** Rótulos em português para a tabela de elementos. */
export const ELEMENT_KIND_LABELS: Record<BpmnElementKind, string> = {
  START_EVENT: 'Evento de início',
  END_EVENT: 'Evento de fim',
  INTERMEDIATE_EVENT: 'Evento intermediário',
  ACTIVITY: 'Atividade',
  GATEWAY: 'Gateway',
  SUBPROCESS: 'Subprocesso',
};

export const ELEMENT_SUBTYPE_LABELS: Record<BpmnElementSubtype, string> = {
  GENERIC_TASK: 'genérica',
  USER_TASK: 'usuário',
  SERVICE_TASK: 'serviço',
  SCRIPT_TASK: 'script',
  EXCLUSIVE_GATEWAY: 'exclusivo (XOR)',
  INCLUSIVE_GATEWAY: 'inclusivo (OR)',
  PARALLEL_GATEWAY: 'paralelo (AND)',
  COMPLEX_GATEWAY: 'complexo',
  OTHER: '—',
};

// --- Participação (Fase 8) ---------------------------------------------------

export interface ParticipationSession {
  id: string;
  studentName: string;
  moduleType: ModuleType;
  startedAt: string;
  endedAt: string | null;
  ongoing: boolean;
}

export const participationApi = {
  /** Sessões cruas de uma atividade — visão do professor, sem agregação. */
  list: (activityId: string) =>
    api.get<{ sessions: ParticipationSession[]; total: number }>(
      `/activities/${activityId}/participation`,
    ),
};

/** Duração em minutos, arredondada. null se a sessão ainda está aberta. */
export function sessionDurationMinutes(session: ParticipationSession): number | null {
  if (!session.endedAt) return null;
  const ms = new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime();
  return Math.max(0, Math.round(ms / 60_000));
}

// --- Análise do Processo (Fase 9, D-156) -------------------------------------

export type ActivityContribution = 'DIRECT' | 'PARTIAL' | 'NONE';
export type ActivityRecommendation =
  | 'ELIMINATE'
  | 'INCORPORATE'
  | 'SIMPLIFY'
  | 'AUTOMATE'
  | 'KEEP';
export type GatewaySimplification = 'AUTOMATE' | 'SIMPLIFY' | 'NONE';
export type TimeUnit = 'MINUTES' | 'HOURS' | 'DAYS';

export interface AnalysisEntry {
  id: string;
  elementId: string;
  kind: BpmnElementKind;
  subtype: BpmnElementSubtype;
  name: string | null;
  order: number;
  // Atividades/subprocessos.
  activityContribution: ActivityContribution | null;
  activityMeetsRequirement: boolean | null;
  activityLossIfRemoved: boolean | null;
  activityRecommendation: ActivityRecommendation | null;
  // Gateways.
  gatewayIsNecessary: boolean | null;
  gatewayCriteriaClear: boolean | null;
  gatewaySimplification: GatewaySimplification | null;
  // Eventos.
  eventIsNecessary: boolean | null;
  eventCausesWait: boolean | null;
  eventWaitReducible: boolean | null;
  // Comuns.
  observation: string | null;
  /** Tempo de esforço (PRD 13.7.1), em segundos. */
  timeSeconds: number | null;
  /** Espera antes do início (PRD 13.7.2), em segundos, e a unidade digitada. */
  waitSeconds: number | null;
  waitUnit: TimeUnit | null;
  updatedAt: string;
  updatedByName: string | null;
  // Derivados pelo backend (PRD 13.6) — nunca calculados aqui, pra nunca
  // divergir da mesma lógica usada na exportação em planilha.
  diagnosis: string | null;
  recommendationLabel: string | null;
}

export interface Analysis {
  id: string;
  createdAt: string;
  generalObservation: string | null;
  bpmnVersionId: string;
  versionNumber: number;
  versionLabel: string | null;
  versionFrozenAt: string;
  entries: AnalysisEntry[];
}

export interface SelectableVersion {
  id: string;
  versionNumber: number;
  label: string | null;
  frozenAt: string;
}

export type AnalysisState =
  | { exists: true; analysis: Analysis }
  | { exists: false; availableVersions: SelectableVersion[] };

export interface UpdateEntryPayload {
  activityContribution?: ActivityContribution | null;
  activityMeetsRequirement?: boolean | null;
  activityLossIfRemoved?: boolean | null;
  activityRecommendation?: ActivityRecommendation | null;
  gatewayIsNecessary?: boolean | null;
  gatewayCriteriaClear?: boolean | null;
  gatewaySimplification?: GatewaySimplification | null;
  eventIsNecessary?: boolean | null;
  eventCausesWait?: boolean | null;
  eventWaitReducible?: boolean | null;
  observation?: string | null;
  timeSeconds?: number | null;
  waitSeconds?: number | null;
  waitUnit?: TimeUnit | null;
}

export const analysisApi = {
  get: (instanceId: string) => api.get<AnalysisState>(`/instances/${instanceId}/analysis`),
  create: (instanceId: string, bpmnVersionId: string) =>
    api.post<{ analysis: Analysis }>(`/instances/${instanceId}/analysis`, { bpmnVersionId }),
  updateEntry: (entryId: string, payload: UpdateEntryPayload) =>
    api.patch<{ entry: AnalysisEntry }>(`/analysis-entries/${entryId}`, payload),
  updateGeneralObservation: (analysisId: string, generalObservation: string | null) =>
    api.patch<{ generalObservation: string | null }>(
      `/analyses/${analysisId}/general-observation`,
      { generalObservation },
    ),
};

/** Só ACTIVITY e SUBPROCESS têm as 4 perguntas de atividade + tempo (PRD 13.5). */
export function isActivityKind(kind: BpmnElementKind): boolean {
  return kind === 'ACTIVITY' || kind === 'SUBPROCESS';
}

export function isGatewayKind(kind: BpmnElementKind): boolean {
  return kind === 'GATEWAY';
}

/** Eventos de início, fim e intermediários — as 3 perguntas de evento. */
export function isEventKind(kind: BpmnElementKind): boolean {
  return kind === 'START_EVENT' || kind === 'END_EVENT' || kind === 'INTERMEDIATE_EVENT';
}

// --- Problemas (Fase 10) -----------------------------------------------------

export interface ProblemLinkedElement {
  id: string;
  elementId: string;
  name: string | null;
  kind: BpmnElementKind;
  order: number;
}

export interface Problem {
  id: string;
  title: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  links: ProblemLinkedElement[];
}

export interface LinkableElements {
  versionNumber: number | null;
  elements: ProblemLinkedElement[];
}

export const problemsApi = {
  list: (instanceId: string) =>
    api.get<{ problems: Problem[]; total: number }>(`/instances/${instanceId}/problems`),
  create: (instanceId: string, title: string, description?: string) =>
    api.post<{ problem: Problem }>(`/instances/${instanceId}/problems`, {
      title,
      ...(description ? { description } : {}),
    }),
  update: (problemId: string, payload: { title?: string; description?: string | null }) =>
    api.patch<{ problem: Problem }>(`/problems/${problemId}`, payload),
  remove: (problemId: string) => api.delete<void>(`/problems/${problemId}`),

  linkableElements: (instanceId: string) =>
    api.get<LinkableElements>(`/instances/${instanceId}/problems/linkable-elements`),
  link: (problemId: string, bpmnElementId: string) =>
    api.post<{ problem: Problem }>(`/problems/${problemId}/links`, { bpmnElementId }),
  unlink: (problemId: string, bpmnElementId: string) =>
    api.delete<{ problem: Problem }>(`/problems/${problemId}/links/${bpmnElementId}`),
};

// --- Ishikawa 4M (Fase 11) ---------------------------------------------------

export type IshikawaCategory = 'METHOD' | 'MANPOWER' | 'MACHINE' | 'MATERIAL';

export const ISHIKAWA_CATEGORY_LABELS: Record<IshikawaCategory, string> = {
  METHOD: 'Método',
  MANPOWER: 'Mão de obra',
  MACHINE: 'Máquina',
  MATERIAL: 'Material',
};

export interface IshikawaCause {
  id: string;
  description: string;
  category: IshikawaCategory;
  positionX: number;
  positionY: number;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  updatedByName: string | null;
}

/** As 5 extremidades fixas do diagrama que podem ser arrastadas (D-153). */
export type IshikawaLayoutNode = 'HEAD' | IshikawaCategory;

export type IshikawaLayout = Partial<Record<IshikawaLayoutNode, { x: number; y: number }>>;

export interface Ishikawa {
  id: string;
  problemId: string;
  problemTitle: string;
  layout: IshikawaLayout;
  createdAt: string;
  versionNumber: number;
  versionLabel: string | null;
  versionFrozenAt: string;
  causes: IshikawaCause[];
}

export type IshikawaState =
  | { exists: true; ishikawa: Ishikawa }
  | { exists: false; availableVersions: SelectableVersion[] };

export interface CreateCausePayload {
  description: string;
  category: IshikawaCategory;
  positionX: number;
  positionY: number;
}

export interface UpdateCausePayload {
  description?: string;
  category?: IshikawaCategory;
  positionX?: number;
  positionY?: number;
}

export const ishikawaApi = {
  get: (problemId: string) => api.get<IshikawaState>(`/problems/${problemId}/ishikawa`),
  create: (problemId: string, bpmnVersionId: string) =>
    api.post<{ ishikawa: Ishikawa }>(`/problems/${problemId}/ishikawa`, { bpmnVersionId }),
  createCause: (ishikawaId: string, payload: CreateCausePayload) =>
    api.post<{ cause: IshikawaCause }>(`/ishikawas/${ishikawaId}/causes`, payload),
  updateCause: (causeId: string, payload: UpdateCausePayload) =>
    api.patch<{ cause: IshikawaCause }>(`/ishikawa-causes/${causeId}`, payload),
  removeCause: (causeId: string) => api.delete<void>(`/ishikawa-causes/${causeId}`),
  updateLayout: (ishikawaId: string, node: IshikawaLayoutNode, positionX: number, positionY: number) =>
    api.patch<{ layout: IshikawaLayout }>(`/ishikawas/${ishikawaId}/layout`, { node, positionX, positionY }),
};

// --- OKR (Fase 12) ------------------------------------------------------------

export type KrDirection = 'HIGHER_BETTER' | 'LOWER_BETTER' | 'WITHIN_RANGE' | 'EXACT_VALUE';

export const KR_DIRECTION_LABELS: Record<KrDirection, string> = {
  HIGHER_BETTER: 'Quanto maior, melhor',
  LOWER_BETTER: 'Quanto menor, melhor',
  WITHIN_RANGE: 'Manter dentro de uma faixa',
  EXACT_VALUE: 'Atingir um valor específico',
};

export interface KrMeasurement {
  id: string;
  measuredAt: string;
  value: number;
  observation: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface KeyResult {
  id: string;
  description: string;
  indicator: string;
  unit: string;
  baseline: number;
  target: number;
  direction: KrDirection;
  deadline: string | null;
  createdAt: string;
  updatedAt: string;
  /** 0–100, ou null se ainda não há nenhuma medição. */
  progress: number | null;
  latestValue: number | null;
  measurements: KrMeasurement[];
}

export interface Objective {
  id: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  keyResults: KeyResult[];
}

export interface CreateKeyResultPayload {
  description: string;
  indicator: string;
  unit: string;
  baseline: number;
  target: number;
  direction: KrDirection;
  deadline?: string | null;
}

export interface CreateMeasurementPayload {
  measuredAt: string;
  value: number;
  observation?: string;
}

export const okrApi = {
  list: (instanceId: string) =>
    api.get<{ objectives: Objective[]; total: number }>(`/instances/${instanceId}/okr/objectives`),
  createObjective: (instanceId: string, description: string) =>
    api.post<{ objective: Objective }>(`/instances/${instanceId}/okr/objectives`, { description }),
  updateObjective: (objectiveId: string, description: string) =>
    api.patch<{ objective: Objective }>(`/okr/objectives/${objectiveId}`, { description }),
  removeObjective: (objectiveId: string) => api.delete<void>(`/okr/objectives/${objectiveId}`),

  createKeyResult: (objectiveId: string, payload: CreateKeyResultPayload) =>
    api.post<{ objective: Objective }>(`/okr/objectives/${objectiveId}/key-results`, payload),
  updateKeyResult: (keyResultId: string, payload: Partial<CreateKeyResultPayload>) =>
    api.patch<{ objective: Objective }>(`/okr/key-results/${keyResultId}`, payload),
  removeKeyResult: (keyResultId: string) =>
    api.delete<{ objective: Objective }>(`/okr/key-results/${keyResultId}`),

  addMeasurement: (keyResultId: string, payload: CreateMeasurementPayload) =>
    api.post<{ objective: Objective }>(`/okr/key-results/${keyResultId}/measurements`, payload),
  removeMeasurement: (measurementId: string) =>
    api.delete<{ objective: Objective }>(`/okr/measurements/${measurementId}`),
};

// --- Exportação para IA (Fase 14) --------------------------------------------

export const exportAiApi = {
  /** Download direto — exclusivo do professor, o backend recusa aluno. */
  url: (classId: string, groupId: string) =>
    `/api/classes/${classId}/groups/${groupId}/export-ia`,
};

// --- Exportação da planilha do projeto, a partir de qualquer módulo (Fase 22, PRD 24.1) ---

export const projectExportApi = {
  /** Professor exporta qualquer projeto; aluno, só o próprio — o backend confere. */
  url: (instanceId: string) => `/api/instances/${instanceId}/export`,
};

// --- Dashboard do professor (Fase 16) ----------------------------------------

export interface DashboardFilters {
  activityId?: string;
  groupId?: string;
  /** Id de `ClassMember` (o mesmo que a lista de alunos da turma usa). */
  studentId?: string;
  moduleType?: ModuleType;
  dateFrom?: string;
  dateTo?: string;
}

export interface DashboardStudentRow {
  studentId: string;
  studentName: string;
  sessionCount: number;
  minutes: number;
}

export interface DashboardGroupRow {
  groupId: string;
  groupName: string;
  sessionCount: number;
  uniqueStudents: number;
  minutes: number;
}

export interface DashboardInteractionRow {
  moduleType: ModuleType;
  eventType: string;
  count: number;
}

export interface DashboardVersionRow {
  id: string;
  versionNumber: number;
  label: string | null;
  frozenAt: string;
  activityTitle: string | null;
  flowType: ActivityFlowType | null;
  ownerName: string | null;
}

export interface DashboardConnectedRow {
  key: string;
  moduleType: ModuleType;
  ownerName: string | null;
  activityTitle: string | null;
  presence: { userId: string; name: string; initials: string }[];
}

export interface DashboardData {
  participation: {
    totalSessions: number;
    uniqueStudents: number;
    totalMinutes: number;
    byStudent: DashboardStudentRow[];
    byGroup: DashboardGroupRow[];
  };
  interactions: DashboardInteractionRow[];
  versions: DashboardVersionRow[];
  connectedNow: DashboardConnectedRow[];
}

export const dashboardApi = {
  get: (classId: string, filters: DashboardFilters) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }
    const query = params.toString();
    return api.get<DashboardData>(`/classes/${classId}/dashboard${query ? `?${query}` : ''}`);
  },
};

// --- Backup (Fase 18) ---------------------------------------------------------

export type BackupFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export const BACKUP_FREQUENCY_LABELS: Record<BackupFrequency, string> = {
  DAILY: 'Diária',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
};

export interface BackupSettings {
  email: string | null;
  frequency: BackupFrequency;
  hour: number;
  retentionCount: number;
  enabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

export type BackupStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface BackupRecord {
  id: string;
  fileName: string;
  sizeBytes: number;
  status: BackupStatus;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  emailSentAt: string | null;
  triggeredBy: { name: string } | null;
}

// --- Plano de Implantação / 5W1H (Fase 19) -----------------------------------

export type ActionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  NOT_STARTED: 'Não iniciada',
  IN_PROGRESS: 'Em andamento',
  DONE: 'Concluída',
};

export interface ImplementationAction {
  id: string;
  what: string;
  why: string | null;
  where: string | null;
  when: string | null;
  who: string | null;
  how: string | null;
  status: ActionStatus;
  observation: string | null;
  keyResultId: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  updatedByName: string | null;
}

export interface ImplementationPlan {
  id: string;
  createdAt: string;
  actions: ImplementationAction[];
}

export interface CreateActionPayload {
  what: string;
  why?: string;
  where?: string;
  when?: string | null;
  who?: string;
  how?: string;
  status?: ActionStatus;
  observation?: string;
  keyResultId?: string | null;
}

export interface UpdateActionPayload {
  what?: string;
  why?: string | null;
  where?: string | null;
  when?: string | null;
  who?: string | null;
  how?: string | null;
  status?: ActionStatus;
  observation?: string | null;
  keyResultId?: string | null;
}

// --- Visão Futura (Fase 22) ---------------------------------------------------

export interface FutureVisionAnswer {
  questionNumber: number;
  answer: string;
  revision: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface FutureVision {
  answers: FutureVisionAnswer[];
}

export const futureVisionApi = {
  get: (instanceId: string) => api.get<FutureVision>(`/instances/${instanceId}/future-vision`),
  updateAnswer: (instanceId: string, questionNumber: number, answer: string, revision: number) =>
    api.patch<FutureVisionAnswer>(
      `/instances/${instanceId}/future-vision/answers/${questionNumber}`,
      { answer, revision },
    ),
  pdfUrl: (instanceId: string) => `/api/instances/${instanceId}/future-vision/pdf`,
};

// --- SIPOC (Fase 23) ---------------------------------------------------------

export type SipocDimension = 'SUPPLIERS' | 'INPUTS' | 'PROCESS' | 'OUTPUTS' | 'CUSTOMERS';

export const SIPOC_DIMENSION_ORDER: SipocDimension[] = [
  'SUPPLIERS',
  'INPUTS',
  'PROCESS',
  'OUTPUTS',
  'CUSTOMERS',
];

export interface SipocItem {
  id: string;
  dimension: SipocDimension;
  position: number;
  text: string;
  revision: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface Sipoc {
  items: SipocItem[];
}

export const sipocApi = {
  get: (instanceId: string) => api.get<Sipoc>(`/instances/${instanceId}/sipoc`),
  createItem: (instanceId: string, dimension: SipocDimension) =>
    api.post<SipocItem>(`/instances/${instanceId}/sipoc/items`, { dimension }),
  updateItem: (instanceId: string, itemId: string, text: string, revision: number) =>
    api.patch<SipocItem>(`/instances/${instanceId}/sipoc/items/${itemId}`, { text, revision }),
  deleteItem: (instanceId: string, itemId: string) =>
    api.delete<void>(`/instances/${instanceId}/sipoc/items/${itemId}`),
  moveItem: (instanceId: string, itemId: string, direction: 'up' | 'down') =>
    api.patch<{ items: SipocItem[] }>(`/instances/${instanceId}/sipoc/items/${itemId}/move`, {
      direction,
    }),
  pdfUrl: (instanceId: string) => `/api/instances/${instanceId}/sipoc/pdf`,
};

export const implementationPlanApi = {
  get: (instanceId: string) =>
    api.get<ImplementationPlan>(`/instances/${instanceId}/implementation-plan`),
  createAction: (instanceId: string, payload: CreateActionPayload) =>
    api.post<ImplementationPlan>(`/instances/${instanceId}/implementation-plan/actions`, payload),
  updateAction: (actionId: string, payload: UpdateActionPayload) =>
    api.patch<ImplementationPlan>(`/implementation-actions/${actionId}`, payload),
  removeAction: (actionId: string) =>
    api.delete<ImplementationPlan>(`/implementation-actions/${actionId}`),
};

export const backupApi = {
  getSettings: () => api.get<BackupSettings>('/backup/settings'),
  updateSettings: (payload: {
    email: string;
    frequency: BackupFrequency;
    hour: number;
    retentionCount: number;
    enabled: boolean;
  }) => api.put<BackupSettings>('/backup/settings', payload),
  history: () => api.get<BackupRecord[]>('/backup/history'),
  run: () => api.post<BackupRecord>('/backup/run'),
  /** Download pela tela (PRD 38.2): rota autenticada, por id. O link por
   * token, que expira, só existe no e-mail. */
  downloadUrl: (id: string) => `/api/backup/${id}/file`,
};

// --- Acesso e identidade (Fase 24) -------------------------------------------

export interface AccessSettings {
  domainRestrictionEnabled: boolean;
  allowedDomains: string[];
  googleEnabled: boolean;
  microsoftEnabled: boolean;
  microsoftTenant: string | null;
}

export interface LoginOptions {
  google: boolean;
  microsoft: boolean;
}

export const accessApi = {
  getSettings: () => api.get<AccessSettings>('/access/settings'),
  updateSettings: (payload: AccessSettings) =>
    api.put<AccessSettings>('/access/settings', payload),
  /** Público — sem autenticação (quem ainda não tem conta também precisa
   * saber quais botões de login social mostrar). */
  loginOptions: () => api.get<LoginOptions>('/access/login-options'),
};

/** Login social (PRD 33.5) — navegação de página inteira, não fetch: o
 * provedor precisa de um redirect real do navegador para a tela de
 * consentimento dele. */
export function oauthStartUrl(provider: 'google' | 'microsoft', inviteToken?: string): string {
  const params = inviteToken ? `?inviteToken=${encodeURIComponent(inviteToken)}` : '';
  return `/api/auth/oauth/${provider}/start${params}`;
}

// --- Cronograma físico / Gantt (Fase 20) -------------------------------------

export interface GanttTask {
  id: string;
  name: string;
  assignee: string | null;
  startDate: string;
  endDate: string;
  progress: number;
  isMilestone: boolean;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  updatedByName: string | null;
  /** Ids de outras tarefas do MESMO cronograma das quais esta depende. */
  dependsOn: string[];
}

export interface GanttPlan {
  id: string;
  createdAt: string;
  tasks: GanttTask[];
}

export interface CreateGanttTaskPayload {
  name: string;
  assignee?: string;
  startDate: string;
  endDate: string;
  progress?: number;
  isMilestone?: boolean;
}

export interface UpdateGanttTaskPayload {
  name?: string;
  assignee?: string | null;
  startDate?: string;
  endDate?: string;
  progress?: number;
  isMilestone?: boolean;
}

export const ganttApi = {
  get: (instanceId: string) => api.get<GanttPlan>(`/instances/${instanceId}/gantt`),
  createTask: (instanceId: string, payload: CreateGanttTaskPayload) =>
    api.post<GanttPlan>(`/instances/${instanceId}/gantt/tasks`, payload),
  updateTask: (taskId: string, payload: UpdateGanttTaskPayload) =>
    api.patch<GanttPlan>(`/gantt-tasks/${taskId}`, payload),
  removeTask: (taskId: string) => api.delete<GanttPlan>(`/gantt-tasks/${taskId}`),
  addDependency: (taskId: string, dependsOnId: string) =>
    api.post<GanttPlan>(`/gantt-tasks/${taskId}/dependencies`, { dependsOnId }),
  removeDependency: (taskId: string, dependsOnId: string) =>
    api.delete<GanttPlan>(`/gantt-tasks/${taskId}/dependencies/${dependsOnId}`),
};

// --- PDCA (Fase 21) -----------------------------------------------------------

export type PdcaCycleStatus = 'OPEN' | 'CLOSED';

export const PDCA_CYCLE_STATUS_LABELS: Record<PdcaCycleStatus, string> = {
  OPEN: 'Em andamento',
  CLOSED: 'Encerrado',
};

export interface PdcaCycle {
  id: string;
  cycleNumber: number;
  status: PdcaCycleStatus;

  planSituation: string | null;
  planObjective: string | null;
  planGoal: string | null;
  planPlanning: string | null;
  planApproach: string | null;

  doWhatWasDone: string | null;
  doExecution: string | null;
  doObservations: string | null;

  checkResult: string | null;
  checkComparison: string | null;
  checkEvidence: string | null;
  checkConclusion: string | null;

  actKeep: string | null;
  actFix: string | null;
  actNextAction: string | null;
  actNeedsNewCycle: boolean | null;

  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  updatedByName: string | null;
}

export interface PdcaProject {
  id: string;
  title: string;
  description: string | null;
  problemId: string | null;
  bpmnProjectId: string | null;
  ishikawaId: string | null;
  implementationPlanId: string | null;
  ganttPlanId: string | null;
  keyResultId: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  cycles: PdcaCycle[];
}

export interface PdcaLinkOption {
  id: string;
  label: string;
}

export interface PdcaLinkableOptions {
  problems: PdcaLinkOption[];
  bpmnProjects: PdcaLinkOption[];
  ishikawas: PdcaLinkOption[];
  implementationPlans: PdcaLinkOption[];
  ganttPlans: PdcaLinkOption[];
  keyResults: PdcaLinkOption[];
}

export interface PdcaLinks {
  problemId?: string | null;
  bpmnProjectId?: string | null;
  ishikawaId?: string | null;
  implementationPlanId?: string | null;
  ganttPlanId?: string | null;
  keyResultId?: string | null;
}

export interface CreatePdcaProjectPayload extends PdcaLinks {
  title: string;
  description?: string;
}

export interface UpdatePdcaProjectPayload extends PdcaLinks {
  title?: string;
  description?: string | null;
}

export type UpdatePdcaCyclePayload = Partial<
  Omit<PdcaCycle, 'id' | 'cycleNumber' | 'createdAt' | 'updatedAt' | 'createdByName' | 'updatedByName'>
>;

export const pdcaApi = {
  list: (instanceId: string) =>
    api.get<{ projects: PdcaProject[]; total: number }>(`/instances/${instanceId}/pdca/projects`),
  linkableOptions: (instanceId: string) =>
    api.get<PdcaLinkableOptions>(`/instances/${instanceId}/pdca/linkable-options`),
  createProject: (instanceId: string, payload: CreatePdcaProjectPayload) =>
    api.post<{ project: PdcaProject }>(`/instances/${instanceId}/pdca/projects`, payload),
  updateProject: (projectId: string, payload: UpdatePdcaProjectPayload) =>
    api.patch<{ project: PdcaProject }>(`/pdca-projects/${projectId}`, payload),
  removeProject: (projectId: string) => api.delete<void>(`/pdca-projects/${projectId}`),
  addCycle: (projectId: string) =>
    api.post<{ project: PdcaProject }>(`/pdca-projects/${projectId}/cycles`, undefined),
  updateCycle: (cycleId: string, payload: UpdatePdcaCyclePayload) =>
    api.patch<{ project: PdcaProject }>(`/pdca-cycles/${cycleId}`, payload),
  removeCycle: (cycleId: string) =>
    api.delete<{ project: PdcaProject }>(`/pdca-cycles/${cycleId}`),
};
